import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Dice5, Users, Crown } from "lucide-react";

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [role, setRole] = useState("participant");
  const [form, setForm] = useState({ email: "", password: "", first_name: "", last_name: "" });
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") {
        const r = await api.post("/auth/register", { ...form, role });
        if (r.data.session_token) localStorage.setItem("session_token", r.data.session_token);
        setUser(r.data.user);
        toast.success("Welcome aboard!");
        navigate("/dashboard");
      } else {
        const r = await api.post("/auth/login", { email: form.email, password: form.password });
        if (r.data.session_token) localStorage.setItem("session_token", r.data.session_token);
        setUser(r.data.user);
        toast.success("Signed in");
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // Persist role choice for post-callback assignment
    sessionStorage.setItem("pending_role", role);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-slate-950 grid lg:grid-cols-2">
      {/* Left decorative */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900/40 border-r border-white/5 relative overflow-hidden">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center">
            <Dice5 className="w-6 h-6 text-slate-950" />
          </div>
          <div>
            <div className="font-mono-data text-xs text-amber-400 tracking-widest">MEEPLE.MATCH</div>
            <div className="text-slate-100 font-semibold">Tournament OS</div>
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500 font-mono-data tracking-widest mb-3">// THE MATH</div>
          <div className="rounded-xl border border-white/10 bg-slate-950/60 p-6 font-mono-data text-sm text-slate-300">
            <div>table_total = 100</div>
            <div>you_earned = 27</div>
            <div className="text-amber-300">share = 27 / 100 = 0.27</div>
            <div className="mt-3 text-slate-500">// averaged across played rounds</div>
            <div className="text-emerald-400">avg = (0.27 + 0.31 + 0.24) / 3</div>
            <div className="text-slate-100 mt-1">= 0.273 → 27.33%</div>
          </div>
          <p className="text-slate-400 text-sm mt-6 max-w-md">
            Fair normalized scoring — your share of <span className="text-amber-300">your table's</span> victory points,
            averaged across the rounds you played. Byes never penalize you and a runaway round at another table can't drag you down.
          </p>
        </div>
        <div className="text-xs text-slate-600 font-mono-data">© {new Date().getFullYear()} — tabletop-first design</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="flex gap-2 mb-8">
            <button onClick={() => setMode("login")} className={`px-4 py-2 rounded-md text-sm ${mode === 'login' ? 'bg-amber-500 text-slate-950 font-semibold' : 'text-slate-400'}`} data-testid="auth-tab-login">Sign In</button>
            <button onClick={() => setMode("register")} className={`px-4 py-2 rounded-md text-sm ${mode === 'register' ? 'bg-amber-500 text-slate-950 font-semibold' : 'text-slate-400'}`} data-testid="auth-tab-register">Create Account</button>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-slate-50">
            {mode === "register" ? "Join the table." : "Welcome back."}
          </h1>
          <p className="mt-2 text-slate-400">
            {mode === "register" ? "Set up your account in seconds." : "Sign in to run or join a tournament."}
          </p>

          {mode === "register" && (
            <div className="mt-8">
              <div className="text-xs text-slate-500 font-mono-data tracking-widest mb-3">I AM A</div>
              <div className="grid grid-cols-2 gap-3">
                <RoleCard active={role === "participant"} onClick={() => setRole("participant")} icon={<Users className="w-5 h-5" />} title="Participant" desc="I want to play" testId="role-participant" />
                <RoleCard active={role === "organizer"} onClick={() => setRole("organizer")} icon={<Crown className="w-5 h-5" />} title="Organizer" desc="I'm running the show" testId="role-organizer" />
              </div>
              {role === "organizer" && (
                <p className="mt-3 text-xs text-slate-400 leading-relaxed" data-testid="organizer-approval-note">
                  To have your organizer status approved, please contact us at{" "}
                  <a href="mailto:BlackSwampGamers@gmail.com" className="text-amber-400 hover:text-amber-300 underline underline-offset-2">
                    BlackSwampGamers@gmail.com
                  </a>{" "}
                  for details.
                </p>
              )}
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-5">
            {mode === "register" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="fn" className="text-slate-300">First name</Label>
                  <Input id="fn" required data-testid="auth-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="bg-slate-900 border-white/10 mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="ln" className="text-slate-300">Last name</Label>
                  <Input id="ln" required data-testid="auth-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="bg-slate-900 border-white/10 mt-1.5" />
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="em" className="text-slate-300">Email</Label>
              <Input id="em" required type="email" data-testid="auth-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-slate-900 border-white/10 mt-1.5" />
            </div>
            <div>
              <Label htmlFor="pw" className="text-slate-300">Password</Label>
              <Input id="pw" required type="password" data-testid="auth-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-slate-900 border-white/10 mt-1.5" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="auth-submit">
              {loading ? "…" : mode === "register" ? "Create Account" : "Sign In"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-slate-500">
            <div className="flex-1 h-px bg-white/10" />
            <span className="font-mono-data tracking-widest">OR</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <Button variant="outline" onClick={google} className="w-full h-12 bg-white text-slate-900 hover:bg-slate-100 border-white/10 font-semibold" data-testid="auth-google">
            <GoogleIcon /> Continue with Google
          </Button>
        </div>
      </div>
    </div>
  );
}

function RoleCard({ active, onClick, icon, title, desc, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`text-left p-4 rounded-xl border transition-all duration-200 ${active ? 'border-amber-500 bg-amber-500/10 -translate-y-0.5' : 'border-white/10 bg-slate-900/40 hover:border-white/20'}`}>
      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${active ? 'bg-amber-500 text-slate-950' : 'bg-slate-800 text-slate-300'}`}>{icon}</div>
      <div className="mt-3 font-semibold text-slate-100">{title}</div>
      <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
    </button>
  );
}

const GoogleIcon = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
);
